/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace negativeBinomial
*/
var negativeBinomial = {};

/**
* @name cdf
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/cdf}
*/
import cdf from './../../../../base/dists/negative-binomial/cdf';
setReadOnly( negativeBinomial, 'cdf', cdf );

/**
* @name NegativeBinomial
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/ctor}
*/
import NegativeBinomial from './../../../../base/dists/negative-binomial/ctor';
setReadOnly( negativeBinomial, 'NegativeBinomial', NegativeBinomial );

/**
* @name kurtosis
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/kurtosis}
*/
import kurtosis from './../../../../base/dists/negative-binomial/kurtosis';
setReadOnly( negativeBinomial, 'kurtosis', kurtosis );

/**
* @name logpmf
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/logpmf}
*/
import logpmf from './../../../../base/dists/negative-binomial/logpmf';
setReadOnly( negativeBinomial, 'logpmf', logpmf );

/**
* @name mean
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/mean}
*/
import mean from './../../../../base/dists/negative-binomial/mean';
setReadOnly( negativeBinomial, 'mean', mean );

/**
* @name mgf
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/mgf}
*/
import mgf from './../../../../base/dists/negative-binomial/mgf';
setReadOnly( negativeBinomial, 'mgf', mgf );

/**
* @name mode
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/mode}
*/
import mode from './../../../../base/dists/negative-binomial/mode';
setReadOnly( negativeBinomial, 'mode', mode );

/**
* @name pmf
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/pmf}
*/
import pmf from './../../../../base/dists/negative-binomial/pmf';
setReadOnly( negativeBinomial, 'pmf', pmf );

/**
* @name quantile
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/quantile}
*/
import quantile from './../../../../base/dists/negative-binomial/quantile';
setReadOnly( negativeBinomial, 'quantile', quantile );

/**
* @name skewness
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/skewness}
*/
import skewness from './../../../../base/dists/negative-binomial/skewness';
setReadOnly( negativeBinomial, 'skewness', skewness );

/**
* @name stdev
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/stdev}
*/
import stdev from './../../../../base/dists/negative-binomial/stdev';
setReadOnly( negativeBinomial, 'stdev', stdev );

/**
* @name variance
* @memberof negativeBinomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial/variance}
*/
import variance from './../../../../base/dists/negative-binomial/variance';
setReadOnly( negativeBinomial, 'variance', variance );


// EXPORTS //

export default negativeBinomial;
